import { create } from "zustand";
import {
  CUT,
  DEFAULT_FOAM_COLOR,
  DEFAULT_EXPORT_QUALITY,
  FLANGE,
  OVERALL_SMOOTH,
  SCULPT,
  type BlockPresetId,
  type ExportQualityId,
  type FoamColorId,
} from "@/config/designer";
import type { DesignerEngine } from "@/core/engine/DesignerEngine";

/**
 * UI state only.
 *
 * Everything heavy — the voxel field, the meshes, the undo stack — lives in
 * `DesignerEngine`, which is a plain object outside React. This store holds the
 * things a component actually needs to re-render for, plus a `revision`
 * counter the engine bumps when its geometry changes.
 */

export type Phase = "setup" | "preparing" | "workspace";
export type ToolId = "carve" | "smooth" | "fixings" | "cut" | "angle" | "flange" | "texture";
export type NavigationMode = "sculpt" | "orbit" | "pan" | "zoom";

export function isCuttingTool(tool: ToolId): boolean {
  return tool === "cut" || tool === "angle";
}

/**
 * "free" is the perspective three-quarter view used for sculpting. The five
 * named views are orthographic elevations — a cut is a parallel sweep along
 * the view direction, so it only makes sense from a parallel projection.
 */
export type ViewMode = "free" | "top" | "front" | "back" | "left" | "right";

export const STANDARD_VIEWS: readonly Exclude<ViewMode, "free">[] = [
  "top",
  "front",
  "back",
  "left",
  "right",
];

export interface ExportStatus {
  state: "idle" | "working" | "done" | "error";
  message?: string;
}

export interface Notice {
  id: number;
  text: string;
  tone: "info" | "warn";
}

interface DesignerState {
  phase: Phase;
  presetId: BlockPresetId;
  engine: DesignerEngine | null;

  tool: ToolId;
  brushRadius: number;
  carveStrength: number;
  smoothStrength: number;
  soundEnabled: boolean;
  baseLocked: boolean;
  baseThickness: number;
  foamColor: FoamColorId;
  flangeWidth: number;
  overallSmoothStrength: number;
  navigationMode: NavigationMode;

  selectedFixingId: string | null;

  viewMode: ViewMode;
  /** Incremented on every view request, so re-picking the current view re-snaps. */
  viewEpoch: number;
  /** The line drawn for the Cut tool, in normalised device coordinates. */
  cutPoints: Array<[number, number]>;
  /** Snapped edge angle shown while the Angle tool is active. */
  cutAngleDegrees: number | null;
  cutting: boolean;

  /** Bumped whenever the engine's geometry or fixings change. */
  revision: number;
  canUndo: boolean;
  canRedo: boolean;
  buildingHoles: boolean;

  exportQuality: ExportQualityId;
  exportStatus: ExportStatus;
  showDiagnostics: boolean;
  showShortcuts: boolean;
  confirmingReset: boolean;
  confirmingNew: boolean;
  showOverallSmooth: boolean;
  showFlangePreview: boolean;
  notices: Notice[];

  setPhase: (phase: Phase) => void;
  setPreset: (id: BlockPresetId) => void;
  setEngine: (engine: DesignerEngine | null) => void;
  setTool: (tool: ToolId) => void;
  setBrushRadius: (radius: number) => void;
  scaleBrushRadius: (factor: number) => void;
  setCarveStrength: (value: number) => void;
  setSmoothStrength: (value: number) => void;
  toggleSound: () => void;
  toggleBaseLock: () => void;
  setBaseThickness: (thickness: number) => void;
  setFoamColor: (color: FoamColorId) => void;
  setFlangeWidth: (width: number) => void;
  setOverallSmoothStrength: (strength: number) => void;
  setNavigationMode: (mode: NavigationMode) => void;
  selectFixing: (id: string | null) => void;
  setViewMode: (view: ViewMode) => void;
  addCutPoint: (point: [number, number]) => void;
  setAngleCut: (
    start: [number, number],
    end: [number, number],
    degrees: number,
  ) => void;
  clearCutPoints: () => void;
  setCutting: (busy: boolean) => void;
  bumpRevision: () => void;
  setHistoryState: (canUndo: boolean, canRedo: boolean) => void;
  setBuildingHoles: (busy: boolean) => void;
  setExportQuality: (quality: ExportQualityId) => void;
  setExportStatus: (status: ExportStatus) => void;
  toggleDiagnostics: () => void;
  setShowShortcuts: (open: boolean) => void;
  setConfirmingReset: (open: boolean) => void;
  setConfirmingNew: (open: boolean) => void;
  setShowOverallSmooth: (open: boolean) => void;
  setShowFlangePreview: (open: boolean) => void;
  notify: (text: string, tone?: Notice["tone"]) => void;
  dismissNotice: (id: number) => void;
}

let noticeId = 0;

export const useDesignerStore = create<DesignerState>((set, get) => ({
  phase: "setup",
  presetId: "medium",
  engine: null,

  tool: "carve",
  brushRadius: 38,
  carveStrength: SCULPT.defaultStrength,
  smoothStrength: SCULPT.defaultSmoothStrength,
  soundEnabled: true,
  baseLocked: false,
  baseThickness: SCULPT.defaultLockedBaseThickness,
  foamColor: DEFAULT_FOAM_COLOR,
  flangeWidth: FLANGE.defaultWidth,
  overallSmoothStrength: OVERALL_SMOOTH.defaultStrength,
  navigationMode: "sculpt",

  selectedFixingId: null,

  viewMode: "free",
  viewEpoch: 0,
  cutPoints: [],
  cutAngleDegrees: null,
  cutting: false,

  revision: 0,
  canUndo: false,
  canRedo: false,
  buildingHoles: false,

  exportQuality: DEFAULT_EXPORT_QUALITY,
  exportStatus: { state: "idle" },
  showDiagnostics: false,
  showShortcuts: false,
  confirmingReset: false,
  confirmingNew: false,
  showOverallSmooth: false,
  showFlangePreview: false,
  notices: [],

  setPhase: (phase) => set({ phase }),
  setPreset: (presetId) => set({ presetId }),
  setEngine: (engine) =>
    set({
      engine,
      brushRadius: engine ? engine.defaultBrushRadius : get().brushRadius,
      selectedFixingId: null,
    }),
  setTool: (tool) =>
    set((s) => ({
      tool,
      selectedFixingId: null,
      cutPoints: [],
      cutAngleDegrees: null,
      navigationMode: "sculpt",
      // A cut is a parallel sweep along the view direction, so entering the
      // tool from the perspective view puts the camera on a square elevation.
      viewMode: isCuttingTool(tool) && s.viewMode === "free" ? "front" : s.viewMode,
    })),
  setBrushRadius: (radius) =>
    set({ brushRadius: clamp(radius, SCULPT.minRadius, SCULPT.maxRadius) }),
  scaleBrushRadius: (factor) =>
    set((s) => ({
      brushRadius: clamp(s.brushRadius * factor, SCULPT.minRadius, SCULPT.maxRadius),
    })),
  setCarveStrength: (value) =>
    set({ carveStrength: clamp(value, SCULPT.minStrength, SCULPT.maxStrength) }),
  setSmoothStrength: (value) =>
    set({ smoothStrength: clamp(value, SCULPT.minStrength, SCULPT.maxSmoothStrength) }),
  toggleSound: () => set((s) => ({ soundEnabled: !s.soundEnabled })),
  toggleBaseLock: () => set((s) => ({ baseLocked: !s.baseLocked })),
  setBaseThickness: (baseThickness) =>
    set({
      baseThickness: clamp(
        baseThickness,
        SCULPT.minLockedBaseThickness,
        SCULPT.maxLockedBaseThickness,
      ),
    }),
  setFoamColor: (foamColor) => set({ foamColor }),
  setFlangeWidth: (flangeWidth) =>
    set({ flangeWidth: clamp(flangeWidth, FLANGE.minWidth, FLANGE.maxWidth) }),
  setOverallSmoothStrength: (overallSmoothStrength) =>
    set({ overallSmoothStrength: clamp(overallSmoothStrength, 0, 100) }),
  setNavigationMode: (navigationMode) => set({ navigationMode }),
  selectFixing: (selectedFixingId) => set({ selectedFixingId }),
  setViewMode: (viewMode) =>
    set((s) => ({
      viewMode,
      viewEpoch: s.viewEpoch + 1,
      // A line drawn from one angle means nothing from another.
      cutPoints: viewMode === s.viewMode ? s.cutPoints : [],
      cutAngleDegrees: viewMode === s.viewMode ? s.cutAngleDegrees : null,
    })),
  addCutPoint: (point) =>
    set((s) => {
      if (s.cutPoints.length >= CUT.maxPoints) return s;
      const last = s.cutPoints[s.cutPoints.length - 1];
      if (last && Math.hypot(point[0] - last[0], point[1] - last[1]) < CUT.minPointSpacingNdc) {
        return s;
      }
      return { cutPoints: [...s.cutPoints, point], cutAngleDegrees: null };
    }),
  setAngleCut: (start, end, cutAngleDegrees) =>
    set({ cutPoints: [start, end], cutAngleDegrees }),
  clearCutPoints: () => set({ cutPoints: [], cutAngleDegrees: null }),
  setCutting: (cutting) => set({ cutting }),
  bumpRevision: () => set((s) => ({ revision: s.revision + 1 })),
  setHistoryState: (canUndo, canRedo) => set({ canUndo, canRedo }),
  setBuildingHoles: (buildingHoles) => set({ buildingHoles }),
  setExportQuality: (exportQuality) => set({ exportQuality }),
  setExportStatus: (exportStatus) => set({ exportStatus }),
  toggleDiagnostics: () => set((s) => ({ showDiagnostics: !s.showDiagnostics })),
  setShowShortcuts: (showShortcuts) => set({ showShortcuts }),
  setConfirmingReset: (confirmingReset) => set({ confirmingReset }),
  setConfirmingNew: (confirmingNew) => set({ confirmingNew }),
  setShowOverallSmooth: (showOverallSmooth) => set({ showOverallSmooth }),
  setShowFlangePreview: (showFlangePreview) => set({ showFlangePreview }),
  notify: (text, tone = "info") =>
    set((s) => ({ notices: [...s.notices, { id: ++noticeId, text, tone }] })),
  dismissNotice: (id) => set((s) => ({ notices: s.notices.filter((n) => n.id !== id) })),
}));

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
